/**
 * 表单联动 Hook
 *
 * 职责：
 * 根据字段变化自动调整关联字段的值
 * - type 切换 → effectDateType / effectVehicleType / effectiveMapList 联动
 * - effectiveMapList 切换 → effectVehicleType 联动（封路障碍物专用）
 * - vehicleLimit 数量限制
 * - effectDateType 切换 → effectTimeType / weekLimit 联动
 * - effectTimeType 切换 → timeLimit 初始化
 */
import { useCallback } from 'react';
import { FormInstance } from 'antd/es/form';
import { message } from 'antd';
import {
  AnchorLayerType,
  EffectDateType,
  EffectVehicleType,
  EffectTimeType,
} from '@/utils/constant';
import { ALL_WEEK_DAYS, DEFAULT_TIME_LIMIT_ITEM } from './useFormInit';
import { getDefaultEffectiveMapList, isHdOnlyEffectiveMap } from '../utils/effectiveMap';
import { normalizeVehicleLimit } from '../utils/vehicleLimit';

interface UseFormLinkageOptions {
  form: FormInstance;
  onRoadTestType?: () => void;
  onLightMapTypeSelect?: () => void;
}

export const useFormLinkage = ({ form, onRoadTestType, onLightMapTypeSelect }: UseFormLinkageOptions) => {
  /**
   * 字段变化回调 - 处理联动规则
   * 通过 Form 的 onValuesChange 触发，只处理值变化，antd 自动同步表单状态
   */
  const onValuesChange = useCallback(
    (changedValues: any, allValues: any) => {
      const updateFields: Record<string, any> = {};
      let shouldUpdate = false;

      // type 字段变化
      if (changedValues.type !== undefined) {
        const type = changedValues.type;
        updateFields.type = type;

        // 封路、禁鸣区以外的类型，默认长期生效
        if (![AnchorLayerType.ROAD_CLOSED, AnchorLayerType.NO_HORN_ZONE].includes(type)) {
          updateFields.effectDateType = EffectDateType.PERMANENT;
        }

        // 部分类型仅允许车辆全部生效（与 master 对齐）
        const allEffectTypes = [
          AnchorLayerType.ATTRIBUTE_ANNOTATION,
          AnchorLayerType.SPEED_LIMIT,
          AnchorLayerType.ROAD_TEST_FOCUS_AREA,
          AnchorLayerType.LD_ASSIST,
          AnchorLayerType.NAVIGATION,
          AnchorLayerType.NO_HORN_ZONE,
        ];
        if (allEffectTypes.includes(type)) {
          updateFields.effectVehicleType = EffectVehicleType.ALL_EFFECT;
          updateFields.vehicleLimit = undefined;
        }

        // 路测关注区：长期生效 + 获取测试项列表
        if (type === AnchorLayerType.ROAD_TEST_FOCUS_AREA) {
          updateFields.effectDateType = EffectDateType.PERMANENT;
          onRoadTestType?.();
        }

        // 轻图测试：清空时间限制与子类型
        if (type === AnchorLayerType.LD_ASSIST) {
          updateFields.effectTimeType = null;
          updateFields.timeLimit = null;
          updateFields.weekLimit = undefined;
          updateFields.subType = undefined;
          // 触发轻图流程回调
          onLightMapTypeSelect?.();
        }

        // 道路导航区：清空时间限制，设置拓扑/方向
        if (type === AnchorLayerType.NAVIGATION) {
          updateFields.effectDateType = EffectDateType.PERMANENT;
          updateFields.effectTimeType = null;
          updateFields.timeLimit = null;
          updateFields.weekLimit = undefined;
          updateFields.topology = undefined;
          updateFields.direction = undefined;
        } else if (type !== AnchorLayerType.NAVIGATION) {
          // 非导航区清空拓扑/方向
          updateFields.topology = undefined;
          updateFields.direction = undefined;
        }

        // 切换到非封路类型时清空生效车范围；切到封路时默认轻图+高精都生效。
        // 默认包含 LD，因此部分车辆生效/不生效需要保持禁用并回到全部生效。
        if (type !== AnchorLayerType.ROAD_CLOSED) {
          updateFields.effectiveMapList = undefined;
        } else {
          const effectiveMapList = allValues.effectiveMapList?.length
            ? allValues.effectiveMapList
            : getDefaultEffectiveMapList();
          updateFields.effectiveMapList = effectiveMapList;
          if (!isHdOnlyEffectiveMap(effectiveMapList)) {
            updateFields.effectVehicleType = EffectVehicleType.ALL_EFFECT;
            updateFields.vehicleLimit = undefined;
          }
        }

        shouldUpdate = true;
      }

      // 生效车范围变化（封路障碍物专用）：联动 effectVehicleType
      if (changedValues.effectiveMapList !== undefined) {
        // 当前已选部分生效，但新的 effectiveMapList 不满足"恰好只 HD"，需重置
        if (
          !isHdOnlyEffectiveMap(changedValues.effectiveMapList) &&
          allValues.effectVehicleType &&
          allValues.effectVehicleType !== EffectVehicleType.ALL_EFFECT
        ) {
          updateFields.effectVehicleType = EffectVehicleType.ALL_EFFECT;
          updateFields.vehicleLimit = undefined;
          shouldUpdate = true;
        }
      }

      // 车辆生效范围切换：全部生效时清空车号；切到部分生效时过滤历史空值，避免 Select 出现空 tag
      if (changedValues.effectVehicleType !== undefined) {
        const value = changedValues.effectVehicleType;
        updateFields.effectVehicleType = value;
        updateFields.vehicleLimit = value === EffectVehicleType.ALL_EFFECT
          ? undefined
          : normalizeVehicleLimit(allValues.vehicleLimit);
        shouldUpdate = true;
      }

      // vehicleLimit 数量限制（最多 50 辆），同时过滤接口/历史数据里的空字符串
      if (changedValues.vehicleLimit !== undefined) {
        const value = changedValues.vehicleLimit;
        const normalizedVehicleLimit = normalizeVehicleLimit(value);
        if (Array.isArray(value) && value.some((item) => !item)) {
          updateFields.vehicleLimit = normalizedVehicleLimit;
          shouldUpdate = true;
        }
        if (normalizedVehicleLimit && normalizedVehicleLimit.length > 50) {
          message.error('最多支持选中50条');
          updateFields.vehicleLimit = normalizedVehicleLimit.slice(0, 50);
          shouldUpdate = true;
        }
      }

      // effectDateType 切换
      if (changedValues.effectDateType !== undefined) {
        const value = changedValues.effectDateType;
        updateFields.effectDateType = value;

        if (value === EffectDateType.SEASONAL) {
          // 周期生效 → 默认全天生效 + 初始化星期
          updateFields.effectTimeType = EffectTimeType.ALL_TIME;
          if (!allValues.weekLimit) {
            updateFields.weekLimit = ALL_WEEK_DAYS;
          }
        } else {
          // 长期生效 → 清空生效时间
          updateFields.effectTimeType = null;
        }

        shouldUpdate = true;
      }

      // effectTimeType 切换
      if (changedValues.effectTimeType !== undefined) {
        const value = changedValues.effectTimeType;
        updateFields.effectTimeType = value;

        if (value === EffectTimeType.ALL_TIME) {
          // 全天生效 → 初始化分时段
          updateFields.timeLimit = [
            { ...DEFAULT_TIME_LIMIT_ITEM, id: new Date().getTime() },
          ];
        }

        shouldUpdate = true;
      }

      // 有联动变化时，批量更新表单
      if (shouldUpdate) {
        form.setFieldsValue(updateFields);
      }
    },
    [form, onRoadTestType, onLightMapTypeSelect]
  );

  return { onValuesChange };
};
